
import { Box, Button, CircularProgress, DialogContent, Grid, Switch, Drawer, Typography } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogMUI from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { styled } from "@mui/material/styles";
import { DeleteDialog, AuthContext, DrawerHeader, Ico_Download, Ico_Filter_Active_Component, Ico_Filter_Component, Ico_MenuDots, Ico_SortAscending, Ico_SortDescending, Ico_Warning, IUser } from '@sagecentral/core-ui';
import { Sort } from 'models/grids';
import { useCurrentUser, useDensityHandler, useGridFilters, useGridMetadata, useGridSorting, usePersistedPageSize } from 'hooks/shared';
import useWorkItemAttachmentsColumns from './useWorkItemAttachmentsColumns';
import { GridBox, GridToolbar, GridNoRowsOverlay } from 'components/shared/grids';
import { usePreference } from 'hooks/preferences';
import { PreferenceType } from 'components/preferences';
import { CentralDataGridPro } from 'components/shared/grids';
import { GridRowId, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { useContext, useEffect, useState } from 'react';
import useWorkItemAttachments from 'hooks/work-items/useWorkItemAttachments';
import { FileOrLink, IAttachmentFormPayload, IFileAttachment } from 'models/attachments';
import useWorkItemAttachmentDownload from 'hooks/work-items/useWorkItemAttachmentDownload';
import useWorkItemAttachmentDelete from 'hooks/work-items/useWorkItemAttachmentDelete';
import { useSnackbar } from '@sagecentral/core-ui';
import WorkItemAttachment from './add/WorkItemAttachment';
import { useWorkItemAttachmentTypeList, useWorkItemContext, useWorkItemPermissions } from 'hooks/work-items';
import WorkItemAttachmentDrawer from './add/WorkItemAttachmentDrawer';
import { customLocaleText } from 'models/grids/HeaderFilterLocaleText';
import useWorkItemDraftContext from 'hooks/work-items/useWorkItemDraftContext';
import { openAttachmentUtils } from 'utils/attachment-utils';

const NoRowsOverlay = () => {
    return (
        <GridNoRowsOverlay text={`No attachments found.`} aria-live="polite" />
    );
};

const FormTitle = styled(Box)({
    fontSize: '22px',
    marginBottom: '10px',
    fontWeight: 'bold'
});

const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: 64,
    height: 32,
    padding: 6,
    '&.Mui-focusVisible': {
        outline: 'none',
        '& .MuiSwitch-track': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
            borderRadius: '16px'
        }
    },
    '& .MuiSwitch-switchBase': {
        margin: 4,
        padding: 0.8,
        transform: "translateX(2px)",
        '&.Mui-checked': {
            transform: "translateX(30px)",
            '& .MuiSwitch-thumb:before': {
                backgroundColor: '#001A69'
            },
            '& + .MuiSwitch-track': {
                backgroundColor: '#FFFFFF',
                opacity: 1,
                borderColor: '#001A69'
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            boxShadow: `0 1.2px 0 3px ${theme.palette.primary.main}`,
            padding: 0
        },
    },
    '& .MuiSwitch-thumb': {
        width: 22,
        height: 22,
        '&:before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: 'white',
            borderStyle: 'solid',
            borderWidth: '1px 1px 1px 1px',
            borderColor: '#001A69'
        },
    },
    '& .MuiSwitch-track': {
        borderRadius: 20 / 2,
        backgroundColor: 'white',
        borderStyle: 'solid',
        borderWidth: '1px 1px 1px 1px',
        opacity: 1,
        "&:after, &:before": {
            fontSize: "12px",
            position: "absolute",
            top: "7px",
            fontWeight: "bold",
            color: '#333333',
            padding: 0.5,
        },
        "&:after": {
            content: "'NO'",
            left: "35px",
        },
        "&:before": {
            content: "'YES'",
            right: "34px"
        }
    },
}));

const StyledErrorButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    color: 'white',
    '&:hover, &:active, &:focus': {
        backgroundColor: '#FF000020',
        color: theme.palette.error.main
    }
}));

type WorkItemAttachmentsGridPageProps = {
    draft?: boolean;
};

const WorkItemAttachmentsGrid = (props: WorkItemAttachmentsGridPageProps) => {
    const { draft } = props;
    const apiRef = useGridApiRef();
    const auth = useContext(AuthContext);

    /**
     * Handles keyboard navigation by focusing the first actionable item when grid receives focus
     */
    const handleGridFocus = (): void => {
        setTimeout(() => {
            // Find and focus the first actionable element with our custom data attribute
            const firstActionableElement = document.querySelector('[data-first-actionable="true"]') as HTMLElement;
            if (firstActionableElement) {
                firstActionableElement.focus();
            }
        }, 50);
    };
    const { displaySnackbar } = useSnackbar();
    const { workItem: workItemDraft, options } = useWorkItemDraftContext();
    const workItemContext = useWorkItemContext();
    const { workItem, template, updateWorkItemOverview } = workItemContext;
    const workItemPermissions = useWorkItemPermissions();
    const [showAllVersions, setShowAllVersions] = useState(false);
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [attachmentIdToDownload, setAttachmentIdToDownload] = useState<{ id: string, name: string }[]>([]);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [attachmentIdToDelete, setAttachmentIdToDelete] = useState<{ id: number | string, name: string }>({ id: 0, name: '' });
    const [deleteAttachmentId, setDeleteAttachmentId] = useState<number | string | undefined>(undefined);
    const [refreshGrid, setRefreshGrid] = useState(false);
    const [openEditAttachment, setOpenEditAttachment] = useState(false);
    const [attachmentPayload, setAttachmentPayload] = useState<IAttachmentFormPayload>();
    const [types] = useWorkItemAttachmentTypeList(draft ? workItemDraft?.id : workItem?.id, draft);

    const defaultSort: Sort = {
        field: 'fileCreatedDate',
        dir: 'desc'
    };

    const openDeleteAttachment = (attachmentId: number | string, attachmentName: string) => {
        setAttachmentIdToDelete({ id: attachmentId, name: attachmentName });
        setDeleteAttachmentId(undefined);
        setOpenDeleteConfirmation(true);
    }

    const viewAttachment = async (
        attachmentId: number | string,
        attachmentSize: string,
        attachmentName: string
    ) => {
        const user = await auth.getUser() as IUser;

        toggleAttachmentLink(attachmentId.toString(), true);
        await openAttachmentUtils(attachmentId, attachmentSize, attachmentName, draft ?? false, displaySnackbar, user.id_token, workItemDraft?.id, false)
        toggleAttachmentLink(attachmentId.toString(), false);

    };

    const openAttachment = async (
        attachmentId: number | string,
        attachmentSize: string,
        attachmentName: string
    ) => {
        const user = await auth.getUser() as IUser;
        toggleAttachmentLink(attachmentId.toString(), true);

        await openAttachmentUtils(attachmentId, attachmentSize, attachmentName, draft ?? false, displaySnackbar, user.id_token, workItemDraft?.id, true)
        toggleAttachmentLink(attachmentId.toString(), false);
    };

    const handleEditAttachment = (attachment: IFileAttachment) => {
        const payload: IAttachmentFormPayload = {
            id: attachment.id ?? attachment.workItemInstanceAttachmentID,
            typeId: attachment.attachmentTypeID,
            name: attachment.attachmentName,
            url: attachment.attachmentURL,
            fileOrLink: attachment.isAttachementFileOrURL == 'File' ? FileOrLink.File : attachment.isAttachementFileOrURL == 'Link' ? FileOrLink.Link : FileOrLink.SFTP,
            filePath: attachment.filePath
        };

        setAttachmentPayload(payload);
        setOpenEditAttachment(true);
    }

    const [preferencesLoaded, settings, setSettings] = usePreference(PreferenceType.WorkItemAttachmentsGrid, false);
    const [columns, visibilityModel, handleColumnVisibilityOrOrderChange] = useWorkItemAttachmentsColumns({
        settings,
        draft,
        workItemPermissions,
        setSettings,
        openDeleteAttachment,
        openAttachment,
        handleEditAttachment,
        viewAttachment
    });

    const [sort, handleSortChange, sortModel] = useGridSorting({
        defaultSort: [defaultSort],
        settings,
        setSettings
    });

    const [filter, handleFilterChange, filterModel] = useGridFilters({
        settings,
        setSettings
    });

    const [pagination, handlePaginationModelChange] = usePersistedPageSize({
        settings,
        setSettings: setSettings,
    });

    const [density, handleDensityChange] = useDensityHandler({
        settings,
        setSettings: setSettings,
    });

    const [deleteResult, isDeleting] = useWorkItemAttachmentDelete(draft ? workItemDraft?.id : workItem?.id, deleteAttachmentId, displaySnackbar, () => { }, draft);

    const gridMetadata = useGridMetadata(filter, sort, settings?.gridSettings, pagination);

    const [data, total, loading, error] = useWorkItemAttachments(gridMetadata, preferencesLoaded, draft ? workItemDraft?.id : workItem?.id, showAllVersions, !isDeleting, !refreshGrid, draft);

    useEffect(() => {
        if (workItem) {
            const reqs = workItem!.requirements ?? [];
            reqs.filter(item => item.tabKey === 'attachments').forEach(item => {
                const foundIndex = data.findIndex(f => f.attachmentTypeID.toString() === item.key);
                item.isDone = foundIndex !== -1;
            });

            updateWorkItemOverview({ ...workItem, requirements: reqs });
        }
    }, [data])
    const isFilterApplied = () => ((gridMetadata.filterParam?.filters.length || 0) > 0);
    const getData = () => (data ?? []);

    const [includeHeaders, setIncludeHeaders] = useState(
        false,
    );
    const [includeOutliers, setExcludeOutliers] = useState(
        true,
    );
    const [outliersFactor, setOutliersFactor] = useState(
        String(10),
    );
    const [expand, setExpand] = useState(true);

    const autosizeOptions = {
        includeHeaders,
        includeOutliers,
        outliersFactor: Number.isNaN(parseFloat(outliersFactor))
            ? 1
            : parseFloat(outliersFactor),
        expand,
    };
    const [loadingDownload] = useWorkItemAttachmentDownload(true, attachmentIdToDownload, draft, workItemDraft?.id!);

    const downloadSelected = () => {
        const selectedRows: { id: string, name: string }[] = [];
        rowSelectionModel.forEach((rowId: GridRowId) => {
            const row = rowId.toString().split('|');
            selectedRows.push({ id: draft ? row[3] : row[0], name: row[1] });
        });
        setAttachmentIdToDownload(selectedRows);
    }
    const deleteAttachment = () => {
        setDeleteAttachmentId(attachmentIdToDelete.id);
        setOpenDeleteConfirmation(false);
    };

    const toggleAttachmentLink = (attachmentId: string, opening: boolean) => {
        const dataRow = apiRef.current?.getSortedRows()?.find(d => d.workItemInstanceAttachmentID === parseInt(attachmentId));
        const id = `${dataRow?.workItemInstanceAttachmentID}|${dataRow?.attachmentName}|${dataRow?.version}|${dataRow?.id}`
        const row = apiRef.current?.getRow(id)
        if (row) {
            row.attachmentIdOpening = opening;
            apiRef.current?.updateRows([row]);
        }
    }

    useEffect(() => {
        const handleStorageChange = (event: any) => {
            if (event.key === 'sharedState') {
                const sharedState = JSON.parse(event.newValue);
                if (sharedState && sharedState.attachmentIdOpening) {
                    toggleAttachmentLink(sharedState.id, true);
                } else {
                    toggleAttachmentLink(sharedState.id, false);
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return (
        <>
            <Grid container sx={{ minWidth: "100%" }}>
                <Grid item xs={6} >
                    <Button 
                        sx={{ml: '0px'}} 
                        disabled={loadingDownload} 
                        onClick={downloadSelected}
                        aria-label="Download selected attachments"
                    > 
                        {<Ico_Download sx={{ width: '20px', height: '20px', fill: '#001A69', mr:'8px' }} aria-hidden="true" />}
                        Download Selected
                    </Button>
                </Grid>
                <Grid sx={{ fontSize: '14px', display: 'flex', mb: '24px' }} item xs={6} justifyContent="flex-end">
                    {!draft &&
                        <Box sx={{ pr: '24px', display: 'flex', alignItems: 'center' }}>
                            <Typography component="label" htmlFor="show-all-versions" id="show-all-versions-label" sx={{ mr: 1 }}>Show all versions:</Typography>
                            <StyledSwitch
                                id="show-all-versions"
                                inputProps={{
                                    'aria-labelledby': 'show-all-versions-label',
                                    'aria-checked': showAllVersions,
                                    role: 'switch'
                                }}
                                value={showAllVersions}
                                checked={showAllVersions}
                                onChange={() => setShowAllVersions(!showAllVersions)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setShowAllVersions(!showAllVersions);
                                    }
                                }}
                            />
                        </Box>
                    }
                    <WorkItemAttachmentDrawer
                        types={types}
                        setRefreshGrid={setRefreshGrid}
                        refreshGrid={refreshGrid}
                        attachments={data}
                        draft={draft}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <GridBox getData={getData}>
                            <Box sx={{ height: "70vh" }}>
                                <CentralDataGridPro
                                    aria-label="Work item attachments grid"
                                    sx={{
                                        '& .MuiDataGrid-columnHeaderCheckbox': {
                                            pl: '0px', pr: '5px'
                                        },
                                        '& .MuiDataGrid-cellCheckbox': {
                                            pr: '6px',
                                            '& .Mui-disabled': {
                                                display: 'none'
                                            }
                                        }
                                    }}
                                    apiRef={apiRef}
                                    rows={getData()}
                                    rowCount={total}
                                    columns={columns}
                                    getRowId={(r: any) => `${r.workItemInstanceAttachmentID}|${r.attachmentName}|${r.version}|${r.id}`}
                                    filterMode="server"
                                    onRowSelectionModelChange={(newRowSelectionModel) => {
                                        setRowSelectionModel(newRowSelectionModel);
                                    }}
                                    rowSelectionModel={rowSelectionModel}
                                    checkboxSelection
                                    keepNonExistentRowsSelected={true}
                                    disableRowSelectionOnClick
                                    isRowSelectable={(params) => params.row.isAttachementFileOrURL !== FileOrLink[FileOrLink.Link]}
                                    filterModel={filterModel}
                                    sortingMode="server"
                                    sortModel={sortModel}
                                    paginationMode='server'
                                    pagination={true}
                                    pageSizeOptions={[10]}
                                    paginationModel={pagination}
                                    sortingOrder={['asc', 'desc']}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: visibilityModel,
                                        }
                                    }}
                                    onSortModelChange={handleSortChange}
                                    onFilterModelChange={handleFilterChange}
                                    onColumnVisibilityModelChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                                    onColumnWidthChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                                    onColumnOrderChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                                    onPaginationModelChange={handlePaginationModelChange}
                                    slots={{
                                        loadingOverlay: () => (<CircularProgress aria-label="Loading attachments" />),
                                        columnMenuSortAscendingIcon: Ico_SortAscending,
                                        columnMenuSortDescendingIcon: Ico_SortDescending,
                                        columnSortedAscendingIcon: Ico_SortAscending,
                                        columnSortedDescendingIcon: Ico_SortDescending,
                                        columnUnsortedIcon: undefined,
                                        moreActionsIcon: Ico_MenuDots,
                                        openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                                        quickFilterIcon: Ico_Filter_Component,
                                        columnFilteredIcon: Ico_Filter_Component,
                                        quickFilterClearIcon: Ico_Filter_Component,
                                        columnMenuFilterIcon: Ico_Filter_Component,
                                        noRowsOverlay: () => NoRowsOverlay(),
                                        toolbar: GridToolbar,
                                    }}
                                    slotProps={{
                                        baseButton: {
                                            tabIndex: 0
                                        },
                                        cell: {
                                            tabIndex: -1
                                        },
                                        columnHeaders: {
                                            tabIndex: -1
                                        },
                                        row: {
                                            tabIndex: -1 // Make rows not focusable directly
                                        }
                                    }}
                                    loading={loading || !data}
                                    headerFilters
                                    localeText={customLocaleText}
                                    getRowClassName={(params: any) =>
                                        params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                                    }
                                    autosizeOptions={autosizeOptions}
                                />
                            </Box>
                        </GridBox>
                    </Box>
                </Grid>
            </Grid>

            <DeleteDialog nameOfItem={attachmentIdToDelete.name}
                open={openDeleteConfirmation}
                onCancel={() => setOpenDeleteConfirmation(!openDeleteConfirmation)}
                onDelete={() => { deleteAttachment() }}
            />
            <Drawer
                anchor={'right'}
                open={openEditAttachment}
                onClose={() => setOpenEditAttachment(false)}
                PaperProps={{
                    sx: {
                        width: '50%',
                        backgroundColor: "rgba(255, 255, 255, 1)"
                    }
                }}
                sx={{ zIndex: 1300 }}
            >
                <DrawerHeader title='Edit Attachment' onClose={() => setOpenEditAttachment(false)} />
                <WorkItemAttachment edit
                    types={types}
                    attachments={data}
                    onClose={() => {
                        setOpenEditAttachment(false);
                    }}
                    onRefresh={() => {
                        setRefreshGrid(!refreshGrid)
                    }}
                    attachmentData={attachmentPayload}
                    draft={draft}
                />
            </Drawer>
        </>
    )
};

export default WorkItemAttachmentsGrid;